/* eslint-disable no-restricted-imports*/
import React, { isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, CircularProgress, TextFieldProps, Typography, TextField as MUITextField, InputBaseProps } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';

import { makeEndAdornmentId, makeFormControlId, makeHelperTextId, makeInputId, makeLabelId, makeStartAdornmentId } from 'ui-lib/test-utils/test-id-generators';
import { useErrorMessageHandler } from 'ui-lib/components/Inputs/FormHelperText/useErrorMessageHandler';
import EllipsedTypography from 'ui-lib/components/Tooltip/EllipsedTypography';
import { createSx } from 'ui-lib/utils';

import { emptyHelperText } from './const';

const sx = createSx({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  hint: {
    display: 'contents',
    ml: 2,
  },
  inputLabel: {
    display: 'flex',
    flexDirection: 'row',
  },
  endAdornment: {
    gap: 1,
  },
});

export type Props = TextFieldProps & {
  clearable?: boolean;
  loading?: boolean;
  onClear?: (defaultValue: TextFieldProps['value']) => void;
  readOnly?: boolean;
  hideHelperText?: boolean;
  name: string;
  testID?: string;
  autoFocus?: boolean;
  maxLength?: number;
  disableLabelShrink?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
};

export type TextFieldOnChangeProps = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

const TextField = ({
  loading,
  clearable = true,
  onClear,
  disabled,
  readOnly,
  testID,
  hideHelperText,
  autoFocus,
  maxLength,
  disableLabelShrink = false,
  startAdornment,
  endAdornment,
  ...props
}: Props) => {
  const { getErrorMessage } = useErrorMessageHandler();
  const { t } = useTranslation();

  const accessabilityTestID = testID || props.name;
  const textLength = props.value ? (props.value as string).length : 0;

  const customStartAdornment = (props.slotProps?.input as InputBaseProps)?.startAdornment;
  const customEndAdornment = (props.slotProps?.input as InputBaseProps)?.endAdornment;

  const showClearIcon = clearable && onClear && !!props.value && !disabled && !readOnly;
  const showErrorIcon = props.error;

  const shouldShrinkLabel = (!!customEndAdornment || showErrorIcon || !!endAdornment) && !disableLabelShrink;

  const getHelperTextElement = () => {
    if (loading) return <CircularProgress size={10} color={props.color} />;
    if (hideHelperText) return null;

    const getHelperText = () => {
      if (readOnly) return t('readOnlyField');

      return props.error
        ? getErrorMessage(props.helperText)
        : props.helperText || emptyHelperText;
    };

    return (
      <Box sx={sx.root}>
        <EllipsedTypography>
          {getHelperText()}
        </EllipsedTypography>
        {maxLength && (
          <Typography
            variant='caption'
            sx={sx.hint}
            data-test-id='text-area-max-length'
          >
            {textLength}/{maxLength}
          </Typography>
        )}
      </Box>
    );
  };

  const renderAdornmentContent = (adornment: React.ReactNode, adornmentTestID: string) => {
    if (typeof adornment === 'string' || typeof adornment === 'number') {
      return <Typography data-test-id={adornmentTestID}>{adornment}</Typography>;
    }

    return adornment;
  };

  const getStartAdornment = () => {
    if (!startAdornment) return customStartAdornment;

    return (
      <>
        <InputAdornment position='start'>
          {renderAdornmentContent(startAdornment, makeStartAdornmentId(accessabilityTestID))}
        </InputAdornment>
        {customStartAdornment}
      </>
    );
  };

  const getEndAdornment = () => {
    if (customEndAdornment) return customEndAdornment;

    if (!showClearIcon && !showErrorIcon && !endAdornment) return;

    return (
      <InputAdornment position='end' sx={sx.endAdornment}>
        {showClearIcon && (
          <CancelIcon
            className='clear-icon'
            data-test-id='cancel-icon'
            onClick={(event) => {
              event.stopPropagation();
              onClear?.(props.type === 'number' ? null : '');
            }}
          />
        )}
        {showErrorIcon && <ErrorIcon className='error-icon' />}
        {renderAdornmentContent(endAdornment, makeEndAdornmentId(accessabilityTestID))}
      </InputAdornment>
    );
  };

  const renderLabel = () => {
    if (!props.label || isValidElement(props.label)) return props.label;

    return <EllipsedTypography>{props.label}</EllipsedTypography>;
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!readOnly) {
      const { value } = event.target;

      if (typeof value === 'string' && value !== value.trim()) {
        event.target.value = value.trim();

        const changeEvent = {
          ...event,
          type: 'change',
        } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

        props.onChange?.(changeEvent);
      }
    }

    props.onBlur?.(event);
  };

  return (
    <MUITextField
      {...props}
      value={props.value === null ? '' : props.value}
      autoFocus={autoFocus}
      data-test-id={makeFormControlId(accessabilityTestID)}
      disabled={disabled}
      label={renderLabel()}
      helperText={getHelperTextElement()}
      margin={props.label ? 'normal' : 'none'}
      focused={readOnly ? false : undefined}
      onBlur={handleBlur}
      slotProps={{
        input: {
          readOnly,
          ...props.slotProps?.input,
          startAdornment: getStartAdornment(),
          endAdornment: getEndAdornment(),
        },
        htmlInput: {
          ...props.slotProps?.htmlInput,
          'data-test-id': makeInputId(accessabilityTestID),
        },
        inputLabel: {
          ...props.slotProps?.inputLabel,
          'data-test-id': makeLabelId(accessabilityTestID),
          ...(shouldShrinkLabel ? { shrink: true } : {}),
          sx: sx.inputLabel,
        },
        formHelperText: {
          'data-test-id': makeHelperTextId(accessabilityTestID),
        },
      }}
    />
  );
};

export default TextField;
